import React, { FC, memo, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { resetPathStops } from "Containers/Stops";
import { setShowSearchOptions } from "Containers/CampusMap";
import StartDestination from "Containers/StartDestination";
import { areEqual, areEqualShallow } from "Utils/areEqual";
import Icon from "Components/Icons";
import { setCurrentView } from "Containers/MapSwitcher/mapSwitcher.Actions";
import styles from "./styles";

import SearchOptionsDirections from "./SearchOptionsDirections";

interface Props {
  suggestions: any[];
  startingPoint: {};
  destination: {};
  setStartingPoint: (val: any) => void;
  setDestination: (val: any) => void;
  setSearchOptionsView: (view: string) => void;
  showDirections: boolean;
  setShowDirections: (setShowDirections: boolean) => void;
  mapSwitcher: any;
  showSwitcher: boolean;
  trailsCount: number;
  instructions: string[];
}

const Directions: FC<any> = memo(
  ({
    suggestions,
    startingPoint,
    destination,
    setStartingPoint,
    setDestination,
    setSearchOptionsView,
    showDirections,
    setShowDirections,
    mapSwitcher,
    showSwitcher,
    trailsCount,
    instructions,
  }: Props) => {
    const useStyles = createUseStyles(styles);
    const classes = useStyles();
    const dispatch = useDispatch();
    const { showDestinations } = useSelector(
      (selectors: any) => selectors.destinations,
      areEqual
    );
    const view = useSelector((store: any) => store.searchOptionsView.view);
    const goToTrails = useCallback(() => {
      setSearchOptionsView("trails");
    }, [setSearchOptionsView]);

    const handleArrowClick = () => {
      if (showDirections) goToTrails();
      else setShowDirections(true);
    };

    const resetDirections = useCallback(() => {
      setDestination({});
      setStartingPoint({});
    }, [setDestination, setStartingPoint]);

    const gotToDestinations = useCallback(() => {
      resetDirections();
      if (showDestinations) {
        dispatch(setShowSearchOptions(true));
      } else {
        dispatch(setShowSearchOptions(false));
      }
      dispatch(resetPathStops());
      setShowDirections(true);
      setSearchOptionsView("destinations");
    }, [
      dispatch,
      resetDirections,
      setShowDirections,
      showDestinations,
      setSearchOptionsView,
    ]);

    const goToCampusMap = () => {
      resetDirections();
      dispatch(setCurrentView("campusmap", false));

      setSearchOptionsView("destinations");
      dispatch(setShowSearchOptions(false));
    };

    const directionsAnimations = {
      open: {
        x: "0%",
        transition: {
          ease: [0.28, 0.57, 0.2, 1],
          duration: 0.5,
        },
      },
      closed: {
        x: "-100%",
        transition: {
          ease: "easeIn",
          duration: 0.25,
        },
      },
      showHeight: {
        overflow: "visible",
        height: "calc(100vh - 29px)",
        transition: {
          ease: "easeIn",
          duration: 0,
        },
      },
      hideHeight: {
        height: "120px",
        transition: {
          ease: "easeIn",
          duration: 0,
          delay: 0.25,
        },
      },
    };

    useEffect(() => {
      if (
        Object.keys(startingPoint).length &&
        Object.keys(destination).length > 1
      )
        setShowDirections(false);
    }, [startingPoint, destination, setShowDirections]);

    return (
      view === "directions" && (
        <motion.div
          animate={showDirections ? "showHeight" : "hideHeight"}
          variants={directionsAnimations}
          className={`directions-test-class ${classes.container}`}
        >
          <div className={!showDirections && classes.hideTopMobile}>
            <div className={classes.searchOptionsContainer}>
              <SearchOptionsDirections
                resetDirections={resetDirections}
                gotToDestinations={gotToDestinations}
                setShowDirections={setShowDirections}
                showDirections={showDirections}
                setSearchOptionsView={setSearchOptionsView}
                usedSwitcher={mapSwitcher?.usedSwitcher}
                showSwitcher={showSwitcher}
                goToCampusMap={goToCampusMap}
                trailsCount={trailsCount}
              />
            </div>
            <div className={classes.spacer} />
          </div>

          <motion.div
            initial={{ x: "-100%" }}
            animate={directionsAnimations.open}
            className={`${classes.startHolder} ${
              showDirections ? "" : classes.mobileWide
            }`}
          >
            {!showDirections && (
              <div className={classes.arrowContainer}>
                <Icon
                  type="leftarrow"
                  events={{
                    onClick: handleArrowClick,
                  }}
                />
              </div>
            )}
            <StartDestination
              showDirections={showDirections}
              suggestions={suggestions}
              startingPoint={startingPoint}
              destination={destination}
              setStartingPoint={setStartingPoint}
              setDestination={setDestination}
              setShowDirections={setShowDirections}
              instructions={instructions}
            />
            {!showDirections && (
              <div className={classes.closeContainer}>
                <Icon
                  type="close"
                  events={{
                    onClick: gotToDestinations,
                  }}
                />
              </div>
            )}
          </motion.div>
        </motion.div>
      )
    );
  },
  areEqualShallow
);

export default Directions;
try {
    // @ts-ignore
    Directions.displayName = "Directions";
    // @ts-ignore
    Directions.__docgenInfo = { "description": "", "displayName": "Directions", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/components/Directions/Directions.tsx#Directions"] = { docgenInfo: Directions.__docgenInfo, name: "Directions", path: "src/shared/components/Directions/Directions.tsx#Directions" };
}
catch (__react_docgen_typescript_loader_error) { }